import React, { useEffect, useMemo, useRef, useState } from "react";
import { createPortal } from "react-dom";
import { toast } from "react-toastify";
import {
  approveImageGenerator,
  getImageGeneratorErrorMessage,
  ImageGeneratorFeedbackReason,
  ImageGeneratorHistoryRecord,
  ImageGeneratorLookView,
  saveImageGeneratorToLibrary,
  setImageGeneratorLike,
  resolveImageGeneratorResultSrc,
  startImageGeneratorDesignSheet,
  submitImageGeneratorFeedback,
} from "../../app/api/imageGenerator";
import {
  helperTextClassName,
  sectionTitleClassName,
} from "./ImageGeneratorControls";
import ImageGeneratorDesignDetails, {
  ImageGeneratorResultItem,
} from "./ImageGeneratorDesignDetails";
import ImageGeneratorFeedbackModal from "./ImageGeneratorFeedbackModal";
import ImageGeneratorLibraryModal from "./ImageGeneratorLibraryModal";
import ImageGeneratorDesignSheetSlider from "./ImageGeneratorDesignSheetSlider";
import ImageGeneratorDesignSheetDownloadModal from "./ImageGeneratorDesignSheetDownloadModal";
import ImageGeneratorRegenComposer, {
  ImageGeneratorRegenComposerHandle,
  ImageGeneratorRegenSubmitPayload,
  ImageGeneratorRegenView,
} from "./ImageGeneratorRegenComposer";
import {
  copyImageGeneratorLook,
  downloadImageGeneratorLook,
  downloadImageGeneratorLookPdf,
  downloadImageGeneratorLooks,
  ImageGeneratorDownloadFormat,
} from "./imageGeneratorLookExport";
import useImageGeneratorPreviews from "./useImageGeneratorPreviews";
import { useImageGeneratorJobs } from "./ImageGeneratorJobsContext";
import {
  CopyIcon,
  DownloadIcon,
  ExpandIcon,
  RegenerateIcon,
  ThumbIcon,
  expandGlyphClassName,
  generativeDownloadMenuClassName,
  generativeDownloadMenuItemClassName,
  generativeResultToolBtnClassName,
  iconGlyphClassName,
} from "./shared/generativeResultToolbarIcons";
import CollapseIcon from "../../assets/icons/CollapseIcon";
import { scrollCollectionSectionIntoView } from "./collectionBuildConstants";
import ImageGeneratorBusyOverlay from "./ImageGeneratorBusyOverlay";

type Props = {
  items: ImageGeneratorResultItem[];
  selectedId: string;
  onSelect: (id: string) => void;
  onRegenerate: (
    generationId: string,
    payload: ImageGeneratorRegenSubmitPayload
  ) => void;
  onRecordUpdated: (record: ImageGeneratorHistoryRecord) => void;
  regenerating?: boolean;
  scrollKey?: number;
};

type LookSlot = ImageGeneratorLookView;
type PendingAction =
  | "like"
  | "feedback"
  | "approve"
  | "library"
  | "download"
  | "copy"
  | "sheet";
type PendingState = { action: PendingAction; lookId: string };

const DOWNLOAD_FORMATS: Array<{
  value: ImageGeneratorDownloadFormat;
  label: string;
}> = [
  { value: "pdf", label: "PDF" },
  { value: "png", label: "PNG" },
  { value: "jpeg", label: "JPEG" },
  { value: "svg", label: "SVG" },
];

const DESIGN_SHEET_DOWNLOAD_FORMATS: Array<{
  value: ImageGeneratorDownloadFormat;
  label: string;
}> = [
  { value: "pdf", label: "PDF" },
  { value: "png", label: "PNG" },
  { value: "jpeg", label: "JPG" },
  { value: "svg", label: "SVG" },
];

const iconButtonClassName = generativeResultToolBtnClassName;

const railButtonClassName =
  "inline-flex h-8 items-center gap-1 rounded-[100px] border border-[#737373] bg-white px-3 py-1 text-xs font-medium leading-none text-secondary hover:bg-gray";

/**
 * Side columns match the results column height on md+ (looks define row height).
 * Same pattern as Collection Build Look Book.
 */
const RESULTS_SIDE_MATCH_HEIGHT_CLASS =
  "flex min-h-0 min-w-0 flex-col overflow-hidden md:h-0 md:min-h-full";

const sheetSlotLabel = (slotId: string, options?: { surface?: boolean }) => {
  if (slotId.endsWith(".apparel")) {
    return "Apparel";
  }
  if (slotId === "pattern.print" || slotId === "pattern.embroidery") {
    return options?.surface ? "Surface Repeat" : "Pattern Repeat";
  }
  if (slotId.endsWith(".print")) {
    return "Print";
  }
  if (slotId.endsWith(".embroidery")) {
    return "Embroidery";
  }
  return "Look";
};

const sheetSlotLabelForDetails = (
  slotId: string,
  details?: { surfaceId?: string } | null
) => sheetSlotLabel(slotId, { surface: Boolean(details?.surfaceId) });

const actionButtonClassName = (
  variant: "generate" | "primary" | "outline",
  enabled: boolean,
  size: "md" | "sm" = "md"
) => {
  const base =
    size === "sm"
      ? variant === "generate"
        ? "h-9 shrink-0 rounded-full px-4 text-xs font-bold whitespace-nowrap transition-colors"
        : "h-9 shrink-0 rounded-full px-4 text-xs font-medium whitespace-nowrap transition-colors"
      : variant === "generate"
        ? "h-10 rounded-full px-8 text-sm font-bold transition-colors"
        : "h-10 rounded-full px-8 text-sm font-medium transition-colors";
  const look =
    variant === "primary"
      ? "bg-black text-white"
      : variant === "generate"
        ? "bg-[#E8E8E8] text-secondary"
        : "border border-[#737373] bg-white text-secondary";
  const hover = enabled
    ? variant === "primary"
      ? "hover:bg-gray-800"
      : variant === "generate"
        ? "hover:bg-[#DDDDDD]"
        : "hover:bg-gray"
    : "cursor-not-allowed opacity-40";
  return `${base} ${look} ${hover}`;
};

const generationIdOf = (item: ImageGeneratorResultItem) => {
  const id = Number(item.id);
  return Number.isInteger(id) && id > 0 ? id : null;
};

const lookFileStem = (item: ImageGeneratorResultItem, slot: LookSlot) =>
  `${item.result.outputType}-${item.id}-${slot}`;

const ImageGeneratorInlineResult: React.FC<Props> = ({
  items,
  selectedId,
  onSelect,
  onRegenerate,
  onRecordUpdated,
  regenerating,
  scrollKey,
}) => {
  const { trackSheetJob } = useImageGeneratorJobs();
  const regenComposerRef = useRef<ImageGeneratorRegenComposerHandle>(null);
  const [regenOpen, setRegenOpen] = useState(false);
  const [regenInitialViews, setRegenInitialViews] = useState<
    ImageGeneratorRegenView[]
  >([]);
  const [regenResetKey, setRegenResetKey] = useState(0);
  const [lightbox, setLightbox] = useState<{
    items: Array<{ src: string; label: string }>;
    index: number;
  } | null>(null);
  const [historyOpen, setHistoryOpen] = useState(true);
  const [downloadMenu, setDownloadMenu] = useState<LookSlot | null>(null);
  const [feedbackView, setFeedbackView] = useState<LookSlot | null>(null);
  const [libraryOpen, setLibraryOpen] = useState(false);
  const [sheetDownloadMenuOpen, setSheetDownloadMenuOpen] = useState(false);
  const [sheetDownloadFormat, setSheetDownloadFormat] =
    useState<ImageGeneratorDownloadFormat | null>(null);
  const [sheetDownloading, setSheetDownloading] = useState(false);
  const [pending, setPending] = useState<PendingState | null>(null);
  const pendingRef = useRef<PendingState | null>(null);
  const mountedRef = useRef(true);
  const sectionRef = useRef<HTMLDivElement>(null);
  const downloadMenuRef = useRef<HTMLDivElement>(null);
  const sheetDownloadMenuRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    mountedRef.current = true;
    return () => {
      mountedRef.current = false;
    };
  }, []);

  const previewItems = useMemo(
    () =>
      items.flatMap(item => {
        const rows = [
          {
            id: `${item.id}:front`,
            imageUrl: item.result.imageUrl,
          },
        ];
        if (item.details.outputType === "Style") {
          rows.push({
            id: `${item.id}:back`,
            imageUrl: item.result.backImageUrl || null,
          });
        }
        (item.details.designSheet?.slots || []).forEach(slot => {
          rows.push({
            id: `${item.id}:sheet:${slot.id}`,
            imageUrl: slot.url,
          });
        });
        return rows;
      }),
    [items]
  );
  const resultPreviews = useImageGeneratorPreviews(previewItems);

  const sortedItems = useMemo(
    () =>
      [...items].sort(
        (left, right) =>
          new Date(right.createdAt).getTime() -
          new Date(left.createdAt).getTime()
      ),
    [items]
  );
  const currentItem = sortedItems[0];
  const previousItems = sortedItems.slice(1);
  const selected =
    sortedItems.find(item => item.id === selectedId) || currentItem;
  const splitStyle = Boolean(
    selected &&
      selected.details.outputType === "Style" &&
      (selected.result.backImageUrl || selected.result.status === "generating")
  );

  useEffect(() => {
    if (!scrollKey) {
      return;
    }
    // Session hydrate expands form sections (e.g. Embroidery) after the first
    // paint, so one early scrollIntoView often stops above Results. Align to
    // the nested page scroller and retry after layout settles.
    let cancelled = false;
    const timers: number[] = [];
    const frames: number[] = [];
    const scrollToResults = () => {
      if (!cancelled) {
        scrollCollectionSectionIntoView(sectionRef.current, "start");
      }
    };
    frames.push(
      window.requestAnimationFrame(() => {
        scrollToResults();
        frames.push(window.requestAnimationFrame(scrollToResults));
      })
    );
    timers.push(window.setTimeout(scrollToResults, 120));
    timers.push(window.setTimeout(scrollToResults, 400));
    return () => {
      cancelled = true;
      frames.forEach(id => window.cancelAnimationFrame(id));
      timers.forEach(id => window.clearTimeout(id));
    };
  }, [scrollKey]);

  useEffect(() => {
    setRegenOpen(false);
    setLightbox(null);
    setDownloadMenu(null);
    setFeedbackView(null);
    setLibraryOpen(false);
  }, [selected?.id]);

  useEffect(() => {
    if (!lightbox) {
      return;
    }
    const previousOverflow = document.body.style.overflow;
    document.body.style.overflow = "hidden";
    const onKeyDown = (event: KeyboardEvent) => {
      if (event.key === "Escape") {
        setLightbox(null);
        return;
      }
      if (!lightbox || lightbox.items.length < 2) {
        return;
      }
      if (event.key === "ArrowLeft") {
        event.preventDefault();
        setLightbox(current =>
          current
            ? {
                ...current,
                index:
                  (current.index - 1 + current.items.length) %
                  current.items.length,
              }
            : current
        );
      }
      if (event.key === "ArrowRight") {
        event.preventDefault();
        setLightbox(current =>
          current
            ? {
                ...current,
                index: (current.index + 1) % current.items.length,
              }
            : current
        );
      }
    };
    window.addEventListener("keydown", onKeyDown);
    return () => {
      document.body.style.overflow = previousOverflow;
      window.removeEventListener("keydown", onKeyDown);
    };
  }, [lightbox]);

  useEffect(() => {
    if (!downloadMenu) {
      return;
    }
    const onPointerDown = (event: MouseEvent) => {
      if (
        downloadMenuRef.current &&
        !downloadMenuRef.current.contains(event.target as Node)
      ) {
        setDownloadMenu(null);
      }
    };
    const onKeyDown = (event: KeyboardEvent) => {
      if (event.key === "Escape") {
        setDownloadMenu(null);
      }
    };
    document.addEventListener("mousedown", onPointerDown);
    window.addEventListener("keydown", onKeyDown);
    return () => {
      document.removeEventListener("mousedown", onPointerDown);
      window.removeEventListener("keydown", onKeyDown);
    };
  }, [downloadMenu]);

  useEffect(() => {
    if (!sheetDownloadMenuOpen) {
      return;
    }
    const onPointerDown = (event: MouseEvent) => {
      if (
        sheetDownloadMenuRef.current &&
        !sheetDownloadMenuRef.current.contains(event.target as Node)
      ) {
        setSheetDownloadMenuOpen(false);
      }
    };
    const onKeyDown = (event: KeyboardEvent) => {
      if (event.key === "Escape") {
        setSheetDownloadMenuOpen(false);
      }
    };
    document.addEventListener("mousedown", onPointerDown);
    window.addEventListener("keydown", onKeyDown);
    return () => {
      document.removeEventListener("mousedown", onPointerDown);
      window.removeEventListener("keydown", onKeyDown);
    };
  }, [sheetDownloadMenuOpen]);

  if (!selected) {
    return null;
  }

  const generationId = generationIdOf(selected);
  const review = selected.result.review || {};
  const isReady =
    selected.result.status === "generated" && Boolean(selected.result.imageUrl);
  const isFailed = selected.result.status === "failed";
  /** Older failed rows may omit retryable; treat omitted as retryable. */
  const canRetryFailed = isFailed && selected.details.retryable !== false;
  const isApproved = Boolean(review.approvedAt);
  const pendingOnThisLook = pending?.lookId === selected.id;
  const actionsLocked = pendingOnThisLook || Boolean(regenerating);
  const canApprove = isReady && !isApproved && !actionsLocked;
  const canSaveOrProceed = isReady && isApproved && !actionsLocked;
  const isInLibrary = Boolean(review.library?.fileName || review.library?.name);
  const libraryDisplayName =
    review.library?.fileName ||
    review.library?.name ||
    (selected.details.outputType === "Style"
      ? [selected.details.productType, selected.details.styleType]
          .filter(Boolean)
          .join(" ")
      : selected.details.microTheme || "Pattern");
  const libraryDefaultFileName =
    review.library?.fileName ||
    review.library?.name ||
    `${selected.result.outputType}-${selected.id}`;

  const beginAction = (action: PendingAction) => {
    if (pendingRef.current) {
      return false;
    }
    const next = { action, lookId: selected.id };
    pendingRef.current = next;
    setPending(next);
    return true;
  };

  const endAction = () => {
    pendingRef.current = null;
    if (mountedRef.current) {
      setPending(null);
    }
  };

  const runLookAction = async (
    action: PendingAction,
    work: (id: number) => Promise<ImageGeneratorHistoryRecord>
  ) => {
    if (!generationId) {
      toast.error("This look is not saved yet. Generate again first.");
      return;
    }
    if (!beginAction(action)) {
      return;
    }
    try {
      const record = await work(generationId);
      onRecordUpdated(record);
      return record;
    } catch (error) {
      toast.error(
        getImageGeneratorErrorMessage(error, "Unable to update this look.")
      );
      return null;
    } finally {
      endAction();
    }
  };

  const openRegen = (views: ImageGeneratorRegenView[]) => {
    if (regenerating || !isReady) {
      return;
    }
    setRegenInitialViews(views);
    setRegenResetKey(value => value + 1);
    setRegenOpen(true);
  };

  /** One-click retry: BE re-queues from stored requestPayload (works for old failed gens). */
  const handleRetryFailed = () => {
    if (!canRetryFailed || regenerating) {
      return;
    }
    if (!generationId) {
      toast.error("This look is not saved yet. Generate again first.");
      return;
    }
    const views: ImageGeneratorRegenView[] =
      selected.details.outputType === "Style" ? ["front", "back"] : [];
    onRegenerate(selected.id, { additionalPrompt: "", views });
  };

  const handleLike = async (slot: LookSlot) => {
    const currentlyLiked = review.likes?.[slot] === true;
    const record = await runLookAction("like", id =>
      setImageGeneratorLike(id, slot, currentlyLiked ? null : true)
    );
    if (record) {
      setFeedbackView(current => (current === slot ? null : current));
      toast.success(currentlyLiked ? "Like removed." : "Liked.");
    }
  };

  const handleDislike = async (slot: LookSlot) => {
    if (review.likes?.[slot] !== false) {
      setFeedbackView(slot);
      return;
    }
    const record = await runLookAction("like", id =>
      setImageGeneratorLike(id, slot, null)
    );
    if (record) {
      toast.success("Dislike removed.");
    }
  };

  const handleCopy = async (previewUrl: string) => {
    if (!beginAction("copy")) {
      return;
    }
    try {
      await copyImageGeneratorLook(previewUrl);
      toast.success("Image copied.");
    } catch {
      toast.error("Unable to copy this image.");
    } finally {
      endAction();
    }
  };

  const handleDownload = async (
    slot: LookSlot,
    previewUrl: string,
    format: ImageGeneratorDownloadFormat
  ) => {
    if (!beginAction("download")) {
      return;
    }
    setDownloadMenu(null);
    try {
      if (format === "pdf") {
        // Full Look / Denim: one PDF with front then back when both exist.
        // Print: single-page PDF of the tile.
        const isStyle = selected.details.outputType === "Style";
        const stylePages = [
          resultPreviews[`${selected.id}:front`]
            ? {
                previewUrl: resultPreviews[`${selected.id}:front`],
                label: "front",
              }
            : null,
          resultPreviews[`${selected.id}:back`]
            ? {
                previewUrl: resultPreviews[`${selected.id}:back`],
                label: "back",
              }
            : null,
        ].filter((page): page is { previewUrl: string; label: string } =>
          Boolean(page?.previewUrl)
        );
        const pdfPages =
          isStyle && stylePages.length > 0
            ? stylePages
            : [{ previewUrl, label: slot }];
        const stem =
          isStyle && pdfPages.length > 1
            ? `${selected.result.outputType}-${selected.id}-look`
            : lookFileStem(selected, slot);
        await downloadImageGeneratorLookPdf(pdfPages, stem);
      } else {
        await downloadImageGeneratorLook(
          previewUrl,
          lookFileStem(selected, slot),
          format
        );
      }
    } catch {
      toast.error("Unable to download this image.");
    } finally {
      endAction();
    }
  };

  const handleFeedbackSubmit = async (payload: {
    reason: ImageGeneratorFeedbackReason;
    details: string;
  }) => {
    if (!feedbackView) {
      return;
    }
    const record = await runLookAction("feedback", id =>
      submitImageGeneratorFeedback(id, {
        view: feedbackView,
        reason: payload.reason,
        details: payload.details,
      })
    );
    if (record) {
      setFeedbackView(null);
      toast.success("Feedback saved.");
    }
  };

  const handleApprove = async () => {
    if (!canApprove) {
      return;
    }
    const record = await runLookAction("approve", approveImageGenerator);
    if (record) {
      toast.success("Look approved.");
    }
  };

  const handleLibrarySave = async (payload: {
    fileName: string;
    name: string;
  }) => {
    const record = await runLookAction("library", id =>
      saveImageGeneratorToLibrary(id, payload)
    );
    if (record) {
      setLibraryOpen(false);
      toast.success("Saved to library.");
    }
  };

  const handleSheetFormatPick = (format: ImageGeneratorDownloadFormat) => {
    setSheetDownloadMenuOpen(false);
    setSheetDownloadFormat(format);
  };

  const handleSheetDownloadConfirm = async (payload: {
    format: ImageGeneratorDownloadFormat;
    slideIds: string[];
    fileName: string;
  }) => {
    if (sheetDownloading) {
      return;
    }
    const slots = selected.details.designSheet?.slots || [];
    const selectedSlides = (
      await Promise.all(
        payload.slideIds.map(async id => {
          const slot = slots.find(item => item.id === id);
          const pngPreview = resultPreviews[`${selected.id}:sheet:${id}`];
          if (!slot || !pngPreview) {
            return null;
          }
          let previewUrl = pngPreview;
          if (payload.format === "svg" && slot.svgUrl) {
            try {
              previewUrl = await resolveImageGeneratorResultSrc(slot.svgUrl);
            } catch {
              previewUrl = pngPreview;
            }
          }
          return {
            id,
            label: sheetSlotLabelForDetails(id, selected.details),
            previewUrl,
            usedStructuredSvg: previewUrl !== pngPreview,
          };
        })
      )
    ).filter(
      (
        slide
      ): slide is {
        id: string;
        label: string;
        previewUrl: string;
        usedStructuredSvg: boolean;
      } => Boolean(slide)
    );
    if (
      payload.format === "svg" &&
      selectedSlides.some(slide => !slide.usedStructuredSvg)
    ) {
      toast(
        "Some slides have no structured SVG yet — those downloads will be a single PNG wrapper (not separate editable images). Regenerate the design sheet for full SVG embeds."
      );
    }
    if (!selectedSlides.length) {
      toast.error("Select at least one sheet to download.");
      return;
    }
    setSheetDownloading(true);
    try {
      const safeName = payload.fileName.trim() || `design-sheet-${selected.id}`;
      if (payload.format === "pdf") {
        await downloadImageGeneratorLookPdf(
          selectedSlides.map(slide => ({
            previewUrl: slide.previewUrl,
            label: slide.label,
          })),
          safeName
        );
      } else {
        await downloadImageGeneratorLooks(
          selectedSlides.map((slide, index) => ({
            previewUrl: slide.previewUrl,
            fileName:
              selectedSlides.length === 1
                ? safeName
                : `${safeName}-sheet-${index + 1}`,
          })),
          payload.format
        );
      }
      setSheetDownloadFormat(null);
      toast.success("Design sheet download started.");
    } catch {
      toast.error("Unable to download design sheet.");
    } finally {
      setSheetDownloading(false);
    }
  };

  const handleProceed = async () => {
    if (!canSaveOrProceed) {
      return;
    }
    const generationId = generationIdOf(selected);
    if (!generationId) {
      return;
    }
    const sheet = selected.details.designSheet;
    if (sheet?.status === "generating") {
      return;
    }
    if (sheet?.status === "generated" && sheet.slots.length) {
      return;
    }
    setPending({ action: "sheet", lookId: selected.id });
    try {
      const record = await startImageGeneratorDesignSheet(generationId);
      onRecordUpdated(record);
      trackSheetJob(record);
      toast.success(
        "Design sheet queued. We’ll notify you in the bell when it’s ready."
      );
    } catch (error) {
      toast.error(
        getImageGeneratorErrorMessage(
          error,
          "Unable to start the design sheet."
        )
      );
    } finally {
      if (mountedRef.current) {
        setPending(null);
      }
    }
  };

  const renderToolbar = (
    slot: LookSlot,
    previewUrl: string,
    regenViews: ImageGeneratorRegenView[],
    enlargeLabel: string
  ) => {
    const liked = review.likes?.[slot] === true;
    const disliked = review.likes?.[slot] === false || feedbackView === slot;
    const toolbarBusy = pendingOnThisLook || Boolean(regenerating);
    return (
      <div className="relative mt-3 flex w-full shrink-0 items-center justify-start gap-2.5 pl-0.5">
        <div
          className="relative"
          ref={downloadMenu === slot ? downloadMenuRef : undefined}
        >
          <button
            type="button"
            onClick={() =>
              setDownloadMenu(current => (current === slot ? null : slot))
            }
            disabled={toolbarBusy}
            className={iconButtonClassName}
            aria-label="Download"
            aria-expanded={downloadMenu === slot}
          >
            <DownloadIcon className={iconGlyphClassName} />
          </button>
          {downloadMenu === slot && (
            <div
              className={`${generativeDownloadMenuClassName} bottom-full left-1/2 mb-2 -translate-x-1/2`}
            >
              {DOWNLOAD_FORMATS.map(option => (
                <button
                  key={option.value}
                  type="button"
                  onClick={() => handleDownload(slot, previewUrl, option.value)}
                  className={generativeDownloadMenuItemClassName}
                >
                  {option.label}
                </button>
              ))}
            </div>
          )}
        </div>
        <button
          type="button"
          onClick={() => void handleLike(slot)}
          disabled={toolbarBusy}
          className={iconButtonClassName}
          aria-label="Like"
          aria-pressed={liked}
        >
          <ThumbIcon filled={liked} className="rotate-180" />
        </button>
        <button
          type="button"
          onClick={() => void handleDislike(slot)}
          disabled={toolbarBusy}
          className={iconButtonClassName}
          aria-label="Dislike"
          aria-pressed={disliked}
        >
          <ThumbIcon filled={disliked} />
        </button>
        <button
          type="button"
          onClick={() => void handleCopy(previewUrl)}
          disabled={toolbarBusy}
          className={iconButtonClassName}
          aria-label="Copy image"
        >
          <CopyIcon className={iconGlyphClassName} />
        </button>
        <button
          type="button"
          onClick={() => openRegen(regenViews)}
          disabled={toolbarBusy || !isReady}
          className={iconButtonClassName}
          aria-label="Regenerate"
          aria-expanded={regenOpen}
        >
          <RegenerateIcon className="h-6 w-6 text-primary" />
        </button>
        <button
          type="button"
          onClick={() =>
            setLightbox({
              items: [{ src: previewUrl, label: enlargeLabel }],
              index: 0,
            })
          }
          disabled={toolbarBusy}
          className={`${iconButtonClassName} ml-auto`}
          aria-label="Enlarge image"
        >
          <ExpandIcon className={expandGlyphClassName} />
        </button>
      </div>
    );
  };

  const renderLookCard = (
    slot: LookSlot,
    previewUrl: string | undefined,
    pendingLabel: string,
    regenViews: ImageGeneratorRegenView[],
    options?: { split?: boolean; showRetry?: boolean }
  ) => {
    const isPattern = selected.details.outputType === "Pattern";
    // Pattern: slightly larger square tile; Style: tall 9:16 — ratio stays fixed while width flexes.
    const frameRatio = isPattern ? "aspect-square" : "aspect-[9/16]";
    const split = Boolean(options?.split);
    const showRetry = options?.showRetry !== false;
    const cardWidthClass = split
      ? "min-w-0 w-[min(100%,340px)] flex-1 basis-[9rem] max-w-[340px]"
      : isPattern
        ? "w-full max-w-[420px]"
        : "w-full max-w-[min(100%,340px)]";

    return (
      <div className={`flex min-w-0 flex-col items-stretch ${cardWidthClass}`}>
        <div
          className={`relative w-full overflow-hidden rounded-[18px] border-[3px] border-[#3D3D3D] bg-white p-1.5 ${frameRatio}`}
        >
          <div className="relative h-full w-full overflow-hidden rounded-[12px] bg-[#F3F3F3]">
            {previewUrl ? (
              <>
                <img
                  src={previewUrl}
                  alt={pendingLabel}
                  className="absolute inset-0 h-full w-full object-cover object-top"
                  decoding="async"
                />
                {regenerating ? (
                  <ImageGeneratorBusyOverlay
                    mode="regen"
                    outputType={selected.details.outputType}
                    variant="inline"
                    compact
                  />
                ) : null}
              </>
            ) : (
              <div className="flex h-full w-full flex-col items-center justify-center px-8 text-center">
                {selected.result.status === "failed" ? (
                  <>
                    <p className="text-sm font-medium text-red-700">
                      Generation failed
                    </p>
                    {selected.details.errorMessage ? (
                      <p className="mt-2 text-xs text-gray-500">
                        {selected.details.errorMessage.replace(
                          /Start a new look\.?/i,
                          "Tap Retry to start again."
                        )}
                      </p>
                    ) : null}
                    {showRetry ? (
                      canRetryFailed ? (
                        <button
                          type="button"
                          onClick={handleRetryFailed}
                          disabled={Boolean(regenerating)}
                          className="mt-4 rounded-full bg-primary px-5 py-2 text-xs font-medium text-white hover:opacity-90 disabled:cursor-not-allowed disabled:opacity-60"
                        >
                          {regenerating ? "Retrying…" : "Retry"}
                        </button>
                      ) : (
                        <p className="mt-3 text-xs text-gray-500">
                          This look can’t be retried. Start a new generation.
                        </p>
                      )
                    ) : null}
                  </>
                ) : selected.result.status === "generating" || regenerating ? (
                  <ImageGeneratorBusyOverlay
                    mode={regenerating ? "regen" : "generate"}
                    outputType={selected.details.outputType}
                    variant="inline"
                    compact
                  />
                ) : (
                  <p className="text-sm font-medium text-gray-700">
                    {pendingLabel}
                  </p>
                )}
              </div>
            )}
          </div>
        </div>
        {previewUrl ? (
          <div className="w-full shrink-0">
            {renderToolbar(slot, previewUrl, regenViews, pendingLabel)}
          </div>
        ) : null}
      </div>
    );
  };

  const renderHistoryThumb = (
    item: ImageGeneratorResultItem,
    badge?: number
  ) => {
    const frontSrc = resultPreviews[`${item.id}:front`];
    const backSrc = resultPreviews[`${item.id}:back`];
    const isStyle = item.details.outputType === "Style";
    const selectedLook = item.id === selected.id;
    return (
      <button
        key={item.id}
        type="button"
        onClick={() => onSelect(item.id)}
        className={`relative w-full overflow-hidden rounded-lg border ${
          selectedLook
            ? "border-gray-900 ring-1 ring-gray-900"
            : "border-gray-200 hover:border-gray-400"
        }`}
      >
        <div
          className={`${
            isStyle ? "aspect-[9/16]" : "aspect-square"
          } flex bg-gray-100`}
        >
          <div className={`${isStyle ? "w-1/2" : "w-full"} h-full`}>
            {frontSrc ? (
              <img
                src={frontSrc}
                alt={`${item.result.outputType} front`}
                className="h-full w-full object-cover object-top"
              />
            ) : (
              <div className="flex h-full w-full items-center justify-center px-0.5 text-center text-[8px] text-gray-400">
                {item.result.status === "generating" ? "…" : "Front"}
              </div>
            )}
          </div>
          {isStyle ? (
            <div className="h-full w-1/2 border-l border-white/80">
              {backSrc ? (
                <img
                  src={backSrc}
                  alt={`${item.result.outputType} back`}
                  className="h-full w-full object-cover object-top"
                />
              ) : (
                <div className="flex h-full w-full items-center justify-center px-0.5 text-center text-[8px] text-gray-400">
                  {item.result.status === "generating" ? "…" : "Back"}
                </div>
              )}
            </div>
          ) : null}
        </div>
        {badge != null && (
          <span className="absolute left-1 top-1 flex h-4 w-4 items-center justify-center rounded-full bg-white text-[9px] font-semibold text-gray-800 shadow">
            {badge}
          </span>
        )}
      </button>
    );
  };

  const frontPreview = resultPreviews[`${selected.id}:front`];
  const backPreview = resultPreviews[`${selected.id}:back`];
  const designSheet = selected.details.designSheet;
  const sheetBusy = designSheet?.status === "generating";
  const sheetFailed = designSheet?.status === "failed";
  const sheetRetryable = sheetFailed && designSheet?.retryable !== false;
  const sheetSlots = designSheet?.slots || [];
  const canStartSheet =
    canSaveOrProceed &&
    !sheetBusy &&
    designSheet?.status !== "generated" &&
    (!sheetFailed || sheetRetryable);
  const generationCount = sortedItems.length;
  const isPatternResult = selected.details.outputType === "Pattern";
  const resultKind = isPatternResult ? "print" : "look";
  const resultStatusMessage = isFailed
    ? canRetryFailed
      ? "Generation failed. Tap Retry to run it again with the same settings."
      : "Generation failed. This look can’t be retried — start a new one."
    : selected.result.status === "generating"
      ? "Generating… hang tight, we’ll update this when it’s ready."
      : generationCount <= 1
        ? `Done! Here’s your ${resultKind}.`
        : generationCount === 2
          ? "Sure, let’s have another go!"
          : generationCount === 3
            ? "Here's another version - let's see if this lands better."
            : generationCount === 4
              ? "Refined it again. Getting closer?"
              : "Final take for now - happy with this one, or want to relook at the brief? Definitely want to nail it the next time!";

  const renderActionBar = (compact: boolean) => {
    const approveButton = (
      <button
        type="button"
        onClick={() => void handleApprove()}
        disabled={!canApprove}
        className={actionButtonClassName("primary", canApprove, "sm")}
      >
        {pendingOnThisLook && pending?.action === "approve"
          ? "Approving..."
          : "Approve"}
      </button>
    );

    return (
      <div className={`${compact ? "mt-3 w-full" : "mt-5 w-full"} min-w-0`}>
        <ImageGeneratorRegenComposer
          ref={regenComposerRef}
          open={regenOpen}
          outputType={selected.details.outputType}
          generationCount={generationCount}
          disabled={regenerating}
          initialViews={regenInitialViews}
          resetKey={regenResetKey}
          onSubmit={payload => onRegenerate(selected.id, payload)}
          onCancel={() => setRegenOpen(false)}
        />
        <div
          className={`${compact ? "mt-3" : "mt-5"} flex w-full min-w-0 flex-col gap-2 sm:flex-row sm:flex-wrap sm:items-center`}
        >
          {/* Approve alone on the left when regen/Generate is open — do not
              group Save / Design Sheet with it or they get dragged left too. */}
          {regenOpen ? approveButton : null}
          <div className="flex w-full min-w-0 flex-nowrap items-center justify-end gap-2 overflow-x-auto sm:ml-auto sm:w-auto">
            {!regenOpen ? approveButton : null}
            <button
              type="button"
              onClick={() =>
                !isInLibrary && canSaveOrProceed && setLibraryOpen(true)
              }
              disabled={!canSaveOrProceed || isInLibrary}
              className={actionButtonClassName(
                "outline",
                canSaveOrProceed && !isInLibrary,
                "sm"
              )}
            >
              {pendingOnThisLook && pending?.action === "library"
                ? "Saving..."
                : isInLibrary
                  ? "Saved"
                  : "Save to Library"}
            </button>
            {isApproved ? (
              <button
                type="button"
                onClick={() => void handleProceed()}
                disabled={!canStartSheet}
                className={actionButtonClassName(
                  "outline",
                  canStartSheet,
                  "sm"
                )}
              >
                {pendingOnThisLook && pending?.action === "sheet"
                  ? "Queuing..."
                  : sheetBusy
                    ? "Building..."
                    : designSheet?.status === "generated"
                      ? "Sheets ready"
                      : sheetFailed && sheetRetryable
                        ? "Retry Design Sheet"
                        : "Design Sheet"}
              </button>
            ) : null}
            {regenOpen ? (
              <button
                type="button"
                onClick={() => regenComposerRef.current?.submit()}
                disabled={Boolean(regenerating)}
                className={actionButtonClassName(
                  "generate",
                  !regenerating,
                  "sm"
                )}
              >
                {regenerating ? "Queuing..." : "Generate"}
              </button>
            ) : null}
          </div>
        </div>
      </div>
    );
  };

  return (
    <div ref={sectionRef} className="border-t border-[#E2E2E2] pt-4">
      <div className="mb-0 flex items-start gap-2">
        <h2
          className={`${sectionTitleClassName} flex flex-wrap items-center gap-x-2`}
        >
          <span>Results</span>
          {isApproved ? (
            <span className="inline-flex items-center gap-1.5 text-sm font-semibold text-[#2E7D32]">
              <span className="h-2 w-2 rounded-full bg-[#2E7D32]" />
              Approved
            </span>
          ) : null}
        </h2>
        {historyOpen ? (
          <button
            type="button"
            onClick={() => setHistoryOpen(false)}
            className={`${railButtonClassName} ml-auto mt-3`}
            aria-label="Collapse history"
          >
            <CollapseIcon fill="#4d4d4d" />
            Collapse
          </button>
        ) : (
          <button
            type="button"
            onClick={() => setHistoryOpen(true)}
            className={`${railButtonClassName} ml-auto mt-3`}
            aria-label="Expand history"
          >
            <CollapseIcon fill="#4d4d4d" />
          </button>
        )}
      </div>
      <p className={`${helperTextClassName} mt-1 mb-4`}>
        {resultStatusMessage}
      </p>

      <div
        className={`grid grid-cols-1 items-stretch gap-4 md:gap-3 lg:gap-5 ${
          isPatternResult
            ? historyOpen
              ? "md:grid-cols-[minmax(7rem,12rem)_minmax(0,1fr)_minmax(4.5rem,6.5rem)]"
              : "md:grid-cols-[minmax(7rem,12rem)_minmax(0,1fr)]"
            : historyOpen
              ? "md:grid-cols-[minmax(7.5rem,13rem)_minmax(0,1fr)_minmax(4.75rem,6.75rem)]"
              : "md:grid-cols-[minmax(7.5rem,13rem)_minmax(0,1fr)]"
        }`}
      >
        <div className={RESULTS_SIDE_MATCH_HEIGHT_CLASS}>
          <ImageGeneratorDesignDetails details={selected.details} />
        </div>

        <section
          className={`flex min-w-0 flex-col self-start ${
            isPatternResult ? "items-center" : ""
          }`}
        >
          {selected.details.outputType === "Style" ? (
            <div className="flex w-full min-w-0 flex-col">
              <div className="flex w-full min-w-0 flex-row flex-wrap items-start justify-center gap-3 sm:gap-4">
                {renderLookCard(
                  "front",
                  frontPreview,
                  selected.result.status === "generating"
                    ? "Generating look…"
                    : selected.result.imageUrl
                      ? "Loading front..."
                      : "Generating front...",
                  ["front"],
                  { split: splitStyle, showRetry: true }
                )}
                {splitStyle &&
                  renderLookCard(
                    "back",
                    backPreview,
                    selected.result.backImageUrl
                      ? "Loading back..."
                      : "Generating back...",
                    ["back"],
                    { split: true, showRetry: false }
                  )}
              </div>
            </div>
          ) : (
            <div className="mx-auto flex w-full max-w-[min(100%,420px)] min-w-0 flex-col items-stretch">
              {renderLookCard(
                "single",
                frontPreview,
                selected.result.imageUrl
                  ? "Loading generated image..."
                  : "Generating image...",
                []
              )}
              {renderActionBar(true)}
            </div>
          )}
        </section>

        {historyOpen ? (
          <aside className={RESULTS_SIDE_MATCH_HEIGHT_CLASS}>
            <div className="flex h-full min-h-0 flex-col gap-3 overflow-hidden">
              {currentItem ? (
                <div className="shrink-0">
                  <p className="mb-1.5 text-[11px] font-medium text-secondary">
                    Current
                  </p>
                  {renderHistoryThumb(currentItem)}
                </div>
              ) : null}
              {previousItems.length > 0 ? (
                <div className="min-h-0 flex-1 overflow-y-auto">
                  <p className="mb-1.5 text-[11px] font-medium text-secondary">
                    Previous
                  </p>
                  <div className="flex flex-col gap-2">
                    {previousItems
                      .slice(0, 12)
                      .map((item, index, visible) =>
                        renderHistoryThumb(item, visible.length - index)
                      )}
                  </div>
                </div>
              ) : null}
            </div>
          </aside>
        ) : null}
      </div>

      {/* Style / denim: keep actions below the full results grid. */}
      {!isPatternResult ? renderActionBar(false) : null}

      {sheetBusy ||
      sheetFailed ||
      (designSheet?.status === "generated" && sheetSlots.length > 0) ? (
        <div className="mt-8 w-full">
          <div className="mb-3 flex flex-wrap items-start justify-between gap-3">
            <div className="min-w-0 flex-1">
              <h3 className={`${sectionTitleClassName} mb-1`}>Design sheet</h3>
              <p className={helperTextClassName}>
                This design was generated by AI for reference and ideation only.
                Please have it reviewed before it&apos;s used for costing,
                sourcing, or production.
              </p>
            </div>
            {designSheet?.status === "generated" && sheetSlots.length > 0 ? (
              <div className="flex shrink-0 items-center gap-2">
                <button
                  type="button"
                  onClick={() =>
                    !isInLibrary && canSaveOrProceed && setLibraryOpen(true)
                  }
                  disabled={!canSaveOrProceed || isInLibrary}
                  className={`${actionButtonClassName(
                    "outline",
                    canSaveOrProceed && !isInLibrary,
                    "sm"
                  )} shrink-0`}
                >
                  {pendingOnThisLook && pending?.action === "library"
                    ? "Saving..."
                    : isInLibrary
                      ? "Saved"
                      : "Save to Library"}
                </button>
                <div className="relative" ref={sheetDownloadMenuRef}>
                  <button
                    type="button"
                    onClick={() => setSheetDownloadMenuOpen(open => !open)}
                    disabled={
                      sheetDownloading ||
                      !sheetSlots.some(
                        slot =>
                          resultPreviews[`${selected.id}:sheet:${slot.id}`]
                      )
                    }
                    className="inline-flex h-9 w-9 items-center justify-center rounded-[10px] border border-[#737373] bg-white text-secondary transition hover:bg-gray disabled:cursor-not-allowed disabled:opacity-40"
                    aria-label="Download design sheet"
                    aria-expanded={sheetDownloadMenuOpen}
                  >
                    <DownloadIcon className="h-5 w-5 [&_path]:fill-current" />
                  </button>
                  {sheetDownloadMenuOpen ? (
                    <div
                      className={`${generativeDownloadMenuClassName} bottom-full right-0 mb-2`}
                    >
                      {DESIGN_SHEET_DOWNLOAD_FORMATS.map(option => (
                        <button
                          key={option.value}
                          type="button"
                          onClick={() => handleSheetFormatPick(option.value)}
                          className={generativeDownloadMenuItemClassName}
                        >
                          {option.label}
                        </button>
                      ))}
                    </div>
                  ) : null}
                </div>
              </div>
            ) : null}
          </div>
          {sheetFailed ? (
            <p className="mb-3 text-sm text-[#B3261E]">
              {designSheet?.errorMessage || "Design sheet failed to generate."}
              {sheetRetryable
                ? " You can tap Retry Design Sheet to try again."
                : ""}
            </p>
          ) : null}
          {/* Keep the loader for the whole generating job — partial slots must
              not hide it or look “ready”. On failure, never render slides. */}
          {sheetBusy ? (
            <div className="relative mx-auto aspect-[3/2] w-full max-w-[36rem] overflow-hidden rounded-2xl border border-[#E8C4B8] bg-[#F7F3EE]">
              <ImageGeneratorBusyOverlay
                mode="generate"
                outputType={selected.details.outputType}
                variant="inline"
                compact
              />
            </div>
          ) : null}
          {designSheet?.status === "generated" && sheetSlots.length > 0 ? (
            <ImageGeneratorDesignSheetSlider
              key={selected.id}
              slides={sheetSlots.map(slot => ({
                id: slot.id,
                label: sheetSlotLabelForDetails(slot.id, selected.details),
                previewUrl: resultPreviews[`${selected.id}:sheet:${slot.id}`],
              }))}
              onOpen={slide => {
                if (!slide.previewUrl) {
                  return;
                }
                const items = sheetSlots
                  .map(slot => ({
                    src: resultPreviews[`${selected.id}:sheet:${slot.id}`],
                    label: sheetSlotLabelForDetails(slot.id, selected.details),
                  }))
                  .filter((item): item is { src: string; label: string } =>
                    Boolean(item.src)
                  );
                const index = items.findIndex(
                  item => item.src === slide.previewUrl
                );
                setLightbox({
                  items,
                  index: index >= 0 ? index : 0,
                });
              }}
            />
          ) : null}
        </div>
      ) : null}

      <ImageGeneratorFeedbackModal
        open={Boolean(feedbackView)}
        submitting={pending?.action === "feedback"}
        onClose={() => {
          if (pending?.action !== "feedback") {
            setFeedbackView(null);
          }
        }}
        onSubmit={handleFeedbackSubmit}
      />
      <ImageGeneratorLibraryModal
        open={libraryOpen}
        submitting={pending?.action === "library"}
        rename={false}
        defaultFileName={libraryDefaultFileName}
        defaultName={libraryDisplayName}
        onClose={() => {
          if (pending?.action !== "library") {
            setLibraryOpen(false);
          }
        }}
        onSubmit={handleLibrarySave}
      />
      <ImageGeneratorDesignSheetDownloadModal
        open={Boolean(sheetDownloadFormat)}
        format={sheetDownloadFormat}
        slides={sheetSlots.map(slot => ({
          id: slot.id,
          label: sheetSlotLabelForDetails(slot.id, selected.details),
          previewUrl: resultPreviews[`${selected.id}:sheet:${slot.id}`],
        }))}
        defaultFileName={`design-sheet-${selected.id}`}
        submitting={sheetDownloading}
        onClose={() => {
          if (!sheetDownloading) {
            setSheetDownloadFormat(null);
          }
        }}
        onDownload={handleSheetDownloadConfirm}
      />

      {lightbox &&
        createPortal(
          <div
            className="fixed inset-0 z-[210] flex items-center justify-center bg-[#1F1F1F]/70 p-4 backdrop-blur-[2px]"
            role="dialog"
            aria-modal="true"
            aria-label="Enlarged image"
            onClick={() => setLightbox(null)}
          >
            <button
              type="button"
              onClick={() => setLightbox(null)}
              className="absolute right-5 top-5 z-20 flex h-10 w-10 items-center justify-center rounded-full bg-white text-secondary shadow"
              aria-label="Close"
            >
              <svg width="18" height="18" viewBox="0 0 24 24" fill="none">
                <path
                  d="M6 6l12 12M18 6 6 18"
                  stroke="currentColor"
                  strokeWidth="1.8"
                  strokeLinecap="round"
                />
              </svg>
            </button>
            <div className="flex max-h-[90vh] max-w-full items-center justify-center gap-1.5 sm:gap-2">
              {lightbox.items.length > 1 ? (
                <button
                  type="button"
                  className="flex h-10 w-10 shrink-0 items-center justify-center rounded-full border border-[#E5D5CE] bg-white text-secondary shadow-md hover:bg-gray sm:h-11 sm:w-11"
                  onClick={event => {
                    event.stopPropagation();
                    setLightbox(current =>
                      current
                        ? {
                            ...current,
                            index:
                              (current.index - 1 + current.items.length) %
                              current.items.length,
                          }
                        : current
                    );
                  }}
                  aria-label="Previous sheet"
                >
                  <svg width="18" height="18" viewBox="0 0 24 24" fill="none">
                    <path
                      d="M15 5 8 12l7 7"
                      stroke="currentColor"
                      strokeWidth="1.8"
                      strokeLinecap="round"
                      strokeLinejoin="round"
                    />
                  </svg>
                </button>
              ) : null}
              <div
                className="flex min-w-0 flex-col items-center"
                onClick={event => event.stopPropagation()}
              >
                <img
                  src={lightbox.items[lightbox.index].src}
                  alt={lightbox.items[lightbox.index].label}
                  className="max-h-[90vh] max-w-[min(calc(96vw-6rem),1400px)] rounded-2xl object-contain shadow-2xl"
                />
                {lightbox.items.length > 1 ? (
                  <p className="mt-3 text-sm text-white/90">
                    {lightbox.items[lightbox.index].label} {lightbox.index + 1}{" "}
                    / {lightbox.items.length}
                  </p>
                ) : null}
              </div>
              {lightbox.items.length > 1 ? (
                <button
                  type="button"
                  className="flex h-10 w-10 shrink-0 items-center justify-center rounded-full border border-[#E5D5CE] bg-white text-secondary shadow-md hover:bg-gray sm:h-11 sm:w-11"
                  onClick={event => {
                    event.stopPropagation();
                    setLightbox(current =>
                      current
                        ? {
                            ...current,
                            index: (current.index + 1) % current.items.length,
                          }
                        : current
                    );
                  }}
                  aria-label="Next sheet"
                >
                  <svg width="18" height="18" viewBox="0 0 24 24" fill="none">
                    <path
                      d="m9 5 7 7-7 7"
                      stroke="currentColor"
                      strokeWidth="1.8"
                      strokeLinecap="round"
                      strokeLinejoin="round"
                    />
                  </svg>
                </button>
              ) : null}
            </div>
          </div>,
          document.body
        )}
    </div>
  );
};

export default ImageGeneratorInlineResult;
